import { z } from 'zod'
import type { EntityIdFactory, ZodIdString } from '../schema/Id'

/**
 * Wraps a Zod object schema into TanStack Router's `params` option format.
 * The parse return type flows into `Route.useParams()` — no manual casts needed.
 */
export const zodParams = <TShape extends z.ZodRawShape>(schema: z.ZodObject<TShape>) => ({
  parse: (raw: Record<string, string>): z.infer<z.ZodObject<TShape>> => schema.parse(raw),
  stringify: (params: z.infer<z.ZodObject<TShape>>): { [K in keyof TShape]: string } =>
    Object.fromEntries(Object.entries(params as Record<string, unknown>).map(([k, v]) => [k, String(v)])) as never,
})

/**
 * Shorthand for the common case where every param is an entity ID.
 *
 * @example
 * params: entityParams({ id: PatientId, relation: PatientPharmacyId })
 * // useParams() returns { id: PatientIdType, relation: PatientPharmacyIdType }
 */
export const entityParams = <T extends Record<string, EntityIdFactory<string>>>(factories: T) =>
  zodParams(
    z.object(
      Object.fromEntries(Object.entries(factories).map(([k, v]) => [k, v.ref])) as {
        [K in keyof T]: T[K] extends EntityIdFactory<infer S> ? ZodIdString<S> : never
      },
    ),
  )
